import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";

const ORG_NAME = "Navdurga Utsav Samiti";
const GOLD = [240, 178, 60];

function formatItemDate(item) {
  return item?.date?.seconds
    ? moment.unix(item.date.seconds).format("DD-MM-YYYY")
    : "";
}

// jsPDF's built-in fonts only cover Latin (WinAnsi) glyphs, so Hindi/Devanagari
// text comes out as garbage. Browsers shape Devanagari correctly on a canvas,
// so any string with other characters is drawn to a canvas and placed in the
// PDF as an image. Latin-only strings keep using real PDF text.
const NEEDS_RASTER =
  /[^\u0000-ÿ–—‘’“”•…]/;
const PX_PER_MM = 8;
const PT_TO_MM = 25.4 / 72;
const FONT_STACK =
  '"Noto Sans Devanagari", "Nirmala UI", "Mangal", "Kohinoor Devanagari", "Noto Sans", Arial, sans-serif';
const PAGE_CONTENT_WIDTH_MM = 182; // A4 (210) minus 14mm margins

const needsRaster = (value) => NEEDS_RASTER.test(String(value ?? ""));

function splitGraphemes(text) {
  if (typeof Intl !== "undefined" && Intl.Segmenter) {
    return Array.from(
      new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(text),
      (part) => part.segment,
    );
  }
  return Array.from(text);
}

function wrapLines(ctx, text, maxWidthPx) {
  const lines = [];
  String(text)
    .split(/\r?\n/)
    .forEach((paragraph) => {
      let line = "";
      paragraph.split(/(\s+)/).forEach((token) => {
        if (!token) return;
        if (ctx.measureText(line + token).width <= maxWidthPx) {
          line += token;
          return;
        }
        if (line.trim()) {
          lines.push(line.trim());
          line = "";
        }
        const word = token.trimStart();
        if (ctx.measureText(word).width <= maxWidthPx) {
          line = word;
          return;
        }
        // a single word wider than the cell: break between characters
        splitGraphemes(word).forEach((grapheme) => {
          if (line && ctx.measureText(line + grapheme).width > maxWidthPx) {
            lines.push(line);
            line = "";
          }
          line += grapheme;
        });
      });
      lines.push(line.trim());
    });
  return lines;
}

// The image gets a solid background (the cell's fill) instead of transparency:
// some mobile PDF viewers render transparent PNGs (alpha masks) incorrectly.
function rasterizeText(
  text,
  {
    fontSizePt,
    widthMm,
    color = "rgb(20,20,20)",
    background = "rgb(255,255,255)",
    singleLine = false,
  },
) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const maxWidthPx = widthMm * PX_PER_MM;
  let size = fontSizePt;
  const applyFont = () => {
    ctx.font = `${size * PT_TO_MM * PX_PER_MM}px ${FONT_STACK}`;
  };

  applyFont();
  if (singleLine) {
    // shrink to fit on one line (used for the header line under the title)
    while (size > 6 && ctx.measureText(text).width > maxWidthPx) {
      size -= 0.5;
      applyFont();
    }
  }
  const lines = singleLine ? [String(text)] : wrapLines(ctx, text, maxWidthPx);
  const lineHeightPx = size * PT_TO_MM * PX_PER_MM * 1.4;

  canvas.width = Math.ceil(maxWidthPx); // resizing resets the context state
  canvas.height = Math.max(1, Math.ceil(lineHeightPx * lines.length));
  applyFont();
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = color;
  ctx.textBaseline = "middle";
  lines.forEach((line, i) => ctx.fillText(line, 0, lineHeightPx * (i + 0.5)));

  return {
    dataUrl: canvas.toDataURL("image/png"),
    widthMm: canvas.width / PX_PER_MM,
    heightMm: canvas.height / PX_PER_MM,
  };
}

// Same call shape as doc.text(): `y` is the text baseline.
function drawText(doc, text, x, y, { fontSize, color }) {
  if (!needsRaster(text)) {
    doc.setFontSize(fontSize);
    doc.setTextColor(...color);
    doc.text(String(text), x, y);
    return;
  }
  const image = rasterizeText(text, {
    fontSizePt: fontSize,
    widthMm: PAGE_CONTENT_WIDTH_MM,
    color: `rgb(${color.join(",")})`,
    singleLine: true,
  });
  const top = y - fontSize * PT_TO_MM * 0.35 - image.heightMm / 2;
  doc.addImage(image.dataUrl, "PNG", x, top, image.widthMm, image.heightMm, undefined, "FAST");
}

// autoTable options that render non-Latin body cells as images. Column widths
// are fixed so each image can be sized to its column before layout runs.
function tableOptions(doc, columnWidthsMm) {
  const images = new Map();
  const keyOf = (data) => `${data.row.index}:${data.column.index}`;
  return {
    columnStyles: Object.fromEntries(
      columnWidthsMm.map((width, index) => [index, { cellWidth: width }]),
    ),
    didParseCell(data) {
      if (data.section !== "body" || !needsRaster(data.cell.raw)) return;
      const padX = data.cell.padding("left") + data.cell.padding("right");
      const padY = data.cell.padding("top") + data.cell.padding("bottom");
      const fill = data.cell.styles.fillColor;
      const image = rasterizeText(data.cell.raw, {
        fontSizePt: data.cell.styles.fontSize,
        widthMm: columnWidthsMm[data.column.index] - padX,
        background: Array.isArray(fill)
          ? `rgb(${fill.slice(0, 3).join(",")})`
          : typeof fill === "number"
          ? `rgb(${fill},${fill},${fill})`
          : "rgb(255,255,255)",
      });
      images.set(keyOf(data), image);
      data.cell.text = [""];
      data.cell.styles.minCellHeight = image.heightMm + padY;
    },
    didDrawCell(data) {
      const image = data.section === "body" && images.get(keyOf(data));
      if (!image) return;
      doc.addImage(
        image.dataUrl,
        "PNG",
        data.cell.x + data.cell.padding("left"),
        data.cell.y + data.cell.padding("top"),
        image.widthMm,
        image.heightMm,
        undefined,
        "FAST",
      );
    },
  };
}

function addHeader(doc, subtitle) {
  doc.setFontSize(16);
  doc.setTextColor(60, 20, 15);
  doc.text(ORG_NAME, 14, 18);
  drawText(doc, subtitle, 14, 25, { fontSize: 11, color: [100, 100, 100] });
  doc.setDrawColor(210);
  doc.line(14, 29, 196, 29);
}

export function buildDaySummaryPDF({
  year,
  donationTotal,
  expenseTotal,
  todayDonations = [],
  todayExpenses = [],
}) {
  const doc = new jsPDF();
  addHeader(doc, `Day-End Summary — ${moment().format("DD MMM YYYY")}`);

  let y = 38;
  doc.setFontSize(11);
  doc.setTextColor(20, 20, 20);
  doc.text(`Financial Year: ${year}`, 14, y);
  y += 8;
  doc.text(`Total Donations (till date): Rs. ${donationTotal.toLocaleString()}`, 14, y);
  y += 7;
  doc.text(`Total Expenses (till date): Rs. ${expenseTotal.toLocaleString()}`, 14, y);
  y += 7;
  doc.setFontSize(12);
  doc.setTextColor(60, 20, 15);
  doc.text(`Net Balance: Rs. ${(donationTotal - expenseTotal).toLocaleString()}`, 14, y);
  y += 10;

  if (todayDonations.length) {
    doc.setFontSize(12);
    doc.setTextColor(20, 20, 20);
    doc.text("Today's Donations", 14, y);
    autoTable(doc, {
      startY: y + 4,
      head: [["Name", "Amount", "Address"]],
      body: todayDonations.map((d) => [
        d.name || "",
        `Rs. ${d.amount}`,
        d.address || "",
      ]),
      theme: "striped",
      headStyles: { fillColor: GOLD, textColor: [40, 20, 10] },
      styles: { fontSize: 9 },
      ...tableOptions(doc, [72, 30, 80]),
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  if (todayExpenses.length) {
    doc.setFontSize(12);
    doc.setTextColor(20, 20, 20);
    doc.text("Today's Expenses", 14, y);
    autoTable(doc, {
      startY: y + 4,
      head: [["Description", "Amount", "Spent By"]],
      body: todayExpenses.map((e) => [
        e.description || "",
        `Rs. ${e.amount}`,
        e.spendBy || "",
      ]),
      theme: "striped",
      headStyles: { fillColor: GOLD, textColor: [40, 20, 10] },
      styles: { fontSize: 9 },
      ...tableOptions(doc, [72, 30, 80]),
    });
  }

  if (!todayDonations.length && !todayExpenses.length) {
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.text("No donation or expense entries recorded today.", 14, y);
  }

  return doc;
}

export function buildRecordsPDF({ collection, records, filterLabel }) {
  const doc = new jsPDF();
  const isExpense = collection === "expense";
  const title = isExpense ? "Expenses Report" : "Donations Report";
  addHeader(doc, `${title}${filterLabel ? ` — ${filterLabel}` : ""}`);

  const total = records.reduce(
    (acc, r) => (!r.isPending ? acc + (Number(r.amount) || 0) : acc),
    0,
  );

  doc.setFontSize(11);
  doc.setTextColor(20, 20, 20);
  doc.text(
    `Total ${isExpense ? "Spent" : "Collected"}: Rs. ${total.toLocaleString()}`,
    14,
    36,
  );
  doc.text(`Entries: ${records.length}`, 14, 43);

  const head = isExpense
    ? [["Description", "Amount", "Spent By", "Date", "Status"]]
    : [["Name", "Amount", "Address", "Date", "Status"]];

  const body = records.map((r) =>
    isExpense
      ? [
          r.description || "",
          `Rs. ${r.amount}`,
          r.spendBy || "",
          formatItemDate(r),
          r.isPending ? "Pending" : "Paid",
        ]
      : [
          r.name || "",
          `Rs. ${r.amount}`,
          r.address || "",
          formatItemDate(r),
          r.isPending ? "Pending" : "Collected",
        ],
  );

  autoTable(doc, {
    startY: 50,
    head,
    body,
    theme: "striped",
    headStyles: { fillColor: GOLD, textColor: [40, 20, 10] },
    styles: { fontSize: 8.5 },
    ...tableOptions(doc, isExpense ? [56, 24, 44, 28, 30] : [50, 24, 56, 26, 26]),
  });

  return doc;
}

export async function sharePDF(doc, fileName, shareText) {
  const blob = doc.output("blob");
  try {
    const file = new File([blob], fileName, { type: "application/pdf" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: fileName, text: shareText });
      return "shared";
    }
  } catch (e) {
    if (e?.name === "AbortError") return "cancelled";
  }
  doc.save(fileName);
  return "downloaded";
}
